import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { Icon } from "@majourneys/icons";
import { useDiscoverCities, useDiscoverItems } from "../../state/discover-context";
import { useTripActions } from "../../state/trip-context";
import { useFocusTrap } from "../../hooks/use-focus-trap";
import { DEFAULT_TRIP_PEOPLE } from "../../state/trip-storage";
import { partyOf } from "../../lib/trip-party";
import { hotelStayHref, isStayItem, ownStay, type TripStayLeg } from "../../lib/trip-stays";
import type { LegStayPrice } from "../../hooks/use-trip-stays";
import type { DiscoverItem, Trip, TripStayRef } from "../../types";
import { formatShortDay } from "./trip-date";
import { StopPrice } from "./stop-price";

/** A catalogue row as the stay it would be. Hotels have a stays page. */
export function stayRefOf(item: DiscoverItem): TripStayRef {
  const hotel = item.segment === "stays" && Boolean(item.detailHref?.includes("/stays/"));
  return {
    kind: hotel ? "hotel" : "venue",
    itemId: item.id,
    slug: item.sourceSlug,
    citySlug: item.citySlug ?? "",
    title: item.title,
    image: item.image || null,
  };
}

/**
 * "Where you sleep" for one city of the route (owner, 2026-11-01: one stay
 * per city, at the start of its days). Empty, it offers a stay; chosen, it
 * shows the stay, its live price for these nights and the party, and a Book
 * button that opens the stay's own page with the dates and guests filled in.
 * Paid online, in full: never in what is due on the day.
 */
export function TripStayCard({
  trip,
  leg,
  price,
  editable,
  onChoose,
  onOpenVenue,
}: {
  trip: Trip;
  leg: TripStayLeg;
  price: LegStayPrice;
  editable: boolean;
  onChoose: () => void;
  /** Open a camp or villa where it can be booked (its detail in the planner). */
  onOpenVenue: (itemId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || "en";
  const cities = useDiscoverCities();
  const city = cities.find((c) => c.slug === leg.city)?.name ?? leg.city;
  const party = partyOf(trip, DEFAULT_TRIP_PEOPLE);
  // Kept whole: "Nov 1 to Nov 3" never breaks before its last number.
  const dates = t("trip.when.range", {
    start: formatShortDay(leg.checkin, 0, lang),
    end: formatShortDay(leg.checkout, 0, lang),
  }).replace(/ /g, "\u00A0");
  // "2 nights · Nov 10 to Nov 12" breaks only at the dot, never inside a part
  // (a lone "2 nights ·" on its own line read as unfinished).
  const nights = t("trip.stay.nights", { count: leg.nights }).replace(/ /g, "\u00A0");

  if (!leg.stay) {
    if (!editable) return null;
    return (
      <button type="button" className="mj-trip-stay mj-trip-stay--empty" onClick={onChoose}>
        <span className="mj-trip-stay__icon" aria-hidden="true">
          <Icon name="ri-hotel-bed-line" />
        </span>
        <span className="mj-trip-stay__body">
          <strong>{t("trip.stay.where_in", { city })}</strong>
          <span>
            {nights}{"\u00A0· "}{dates}
          </span>
        </span>
        <span className="mj-trip-stay__cta">{t("trip.stay.choose")}</span>
      </button>
    );
  }

  const stay = leg.stay;
  const own = stay.kind === "own";
  const priceLine = own
    ? stay.address || t("trip.stay.own_booked")
    : price.state === "priced"
      ? price.text
      : price.state === "loading"
        ? t("trip.stay.pricing")
        : price.state === "full"
          ? t("trip.stay.full")
          : t("trip.stay.price_on_page");

  return (
    <div className="mj-trip-stay" data-testid="trip-stay-card">
      <span
        className="mj-trip-stay__thumb"
        style={stay.image ? { backgroundImage: `url(${stay.image})` } : undefined}
        aria-hidden="true"
      >
        {stay.image ? null : <Icon name="ri-hotel-bed-line" />}
      </span>
      <span className="mj-trip-stay__body">
        <span className="mj-trip-stay__kicker">{t("trip.stay.where_in", { city })}</span>
        <strong>{stay.title}</strong>
        <span>
          {nights}{"\u00A0· "}{dates}
        </span>
        <span className={own ? undefined : price.state === "full" ? "mj-trip-stay__warn" : "mj-trip-stay__price"}>
          {priceLine}
          {price.state === "priced" ? <small> · {t("trip.stay.paid_online")}</small> : null}
        </span>
      </span>
      <span className="mj-trip-stay__actions">
        {editable ? (
          <button type="button" className="mj-trip-stay__change" onClick={onChoose}>
            {t("trip.stay.change")}
          </button>
        ) : null}
        {own ? null : stay.kind === "hotel" ? (
          <a className="mj-trip-stay__book" href={hotelStayHref(lang, stay, leg, party)}>
            {t("trip.stay.book")}
          </a>
        ) : (
          <button type="button" className="mj-trip-stay__book" onClick={() => onOpenVenue(stay.itemId)}>
            {t("trip.stay.book")}
          </button>
        )}
      </span>
    </div>
  );
}

/**
 * Choose where to sleep in one city: its hotels, riads, villas and camps,
 * cheapest first. One tap sets the stay for every night there.
 */
export function StayPickerDialog({
  trip,
  leg,
  onClose,
}: {
  trip: Trip;
  leg: TripStayLeg;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const items = useDiscoverItems();
  const cities = useDiscoverCities();
  const { setLegStay } = useTripActions();
  const dialogRef = useRef<HTMLDivElement>(null);
  const city = cities.find((c) => c.slug === leg.city)?.name ?? leg.city;
  useFocusTrap(dialogRef, true);
  // The guest's own place, prefilled when that is what the city has now.
  const current = leg.stay?.kind === "own" ? leg.stay : null;
  const [ownName, setOwnName] = useState(current?.title ?? "");
  const [ownAddress, setOwnAddress] = useState(current?.address ?? "");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const options = useMemo(
    () =>
      items
        .filter((it) => isStayItem(it) && it.citySlug === leg.city)
        .sort((a, b) => (a.priceMad ?? Infinity) - (b.priceMad ?? Infinity)),
    [items, leg.city],
  );

  const pick = (stay: TripStayRef | null) => {
    setLegStay(trip.id, leg.nightDayIds, stay);
    onClose();
  };

  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      className="mj-trip-dialog-backdrop mj-trip-dialog-backdrop--full"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="mj-trip-dialog mj-trip-dialog--wide mj-add-stop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mj-stay-picker-title"
      >
        <header className="mj-trip-dialog__header">
          <div>
            <span>{t("trip.stay.nights", { count: leg.nights })}</span>
            <strong id="mj-stay-picker-title">{t("trip.stay.where_in", { city })}</strong>
          </div>
          <button type="button" className="mj-trip-dialog__close" onClick={onClose} aria-label={t("common.close")}>
            <Icon name="ri-close-line" aria-hidden="true" />
          </button>
        </header>

        {/* Their own place first (owner, 2026-11-03): we suggest stays, we
            never make them pick one of ours. */}
        <form
          className="mj-trip-stay-own"
          onSubmit={(e) => {
            e.preventDefault();
            const stay = ownStay(leg.city, ownName, ownAddress);
            if (stay) pick(stay);
          }}
        >
          <strong>{t("trip.stay.own_title")}</strong>
          <input
            type="text"
            value={ownName}
            onChange={(e) => setOwnName(e.target.value)}
            placeholder={t("trip.stay.own_name_placeholder")}
            aria-label={t("trip.stay.own_name")}
            maxLength={80}
          />
          <input
            type="text"
            value={ownAddress}
            onChange={(e) => setOwnAddress(e.target.value)}
            placeholder={t("trip.stay.own_address")}
            aria-label={t("trip.stay.own_address")}
            maxLength={160}
          />
          <button type="submit" className="mj-trip-stay__book" disabled={ownName.trim().length === 0}>
            {t("trip.stay.own_add")}
          </button>
        </form>
        {options.length > 0 ? <p className="mj-trip-stay-own__or">{t("trip.stay.ours_title")}</p> : null}

        <div className="mj-add-stop__list">
          {options.length === 0 ? (
            <div className="mj-add-stop__empty">
              <Icon name="ri-hotel-bed-line" aria-hidden="true" />
              <p className="mj-add-stop__empty-title">{t("trip.stay.none_in", { city })}</p>
            </div>
          ) : (
            options.map((item) => {
              const on = leg.stay?.itemId === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`mj-add-stop__item${on ? " is-added" : ""}`}
                  onClick={() => pick(stayRefOf(item))}
                  aria-pressed={on}
                >
                  <span
                    className="mj-add-stop__thumb"
                    style={{ backgroundImage: `url(${item.image})` }}
                    aria-hidden="true"
                  />
                  <span className="mj-add-stop__body">
                    <strong>{item.title}</strong>
                    <span>
                      <Icon name="ri-map-pin-line" aria-hidden="true" />
                      {item.location}
                    </span>
                    <StopPrice item={item} />
                  </span>
                  <span className="mj-add-stop__add" aria-hidden="true">
                    <Icon name={on ? "ri-check-line" : "ri-add-line"} />
                  </span>
                </button>
              );
            })
          )}
        </div>

        {leg.stay ? (
          <footer className="mj-trip-dialog__footer mj-add-stop__footer">
            <button type="button" className="mj-trip-dialog__cancel" onClick={() => pick(null)}>
              {t("trip.stay.remove")}
            </button>
          </footer>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
